/**
 * Maskavo page loader.
 * - First visit: renders immediately, without an overlay.
 * - Internal navigations: show the static SVG only until the next document is parsed.
 */
(() => {
  const loader = document.getElementById('mk-page-loader');
  if (!loader) return;

  const STORAGE_NEXT_KEY = 'mk-loader-next';
  const root = document.documentElement;

  const writeStorage = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Ignore storage failures.
    }
  };

  const removeStorage = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Ignore storage failures.
    }
  };

  const mode = root.dataset.mkLoaderMode || '';
  const shouldStartVisible = mode === 'transition';

  let hideTimer = 0;
  let hidden = false;
  let pageLoaded = document.readyState === 'complete';

  const setVisible = (visible) => {
    root.classList.toggle('mk-loader-pending', visible);
    loader.classList.toggle('is-active', visible);
    loader.setAttribute('aria-busy', visible ? 'true' : 'false');
  };

  const setLoaderMode = (loaderMode) => {
    if (loaderMode) {
      loader.dataset.loaderMode = loaderMode;
    } else {
      delete loader.dataset.loaderMode;
    }
    loader.classList.remove('is-complete');
  };

  const clearTransition = () => {
    removeStorage(STORAGE_NEXT_KEY);
    delete root.dataset.mkLoaderMode;
  };

  const hideNow = () => {
    if (hidden) return;
    hidden = true;
    clearTimeout(hideTimer);
    setVisible(false);
    clearTransition();
  };

  const scheduleHide = () => {
    if (!shouldStartVisible || hidden || !pageLoaded) return;

    clearTimeout(hideTimer);
    hideTimer = window.setTimeout(hideNow, 0);
  };

  const showForOutgoingNavigation = () => {
    hidden = false;
    writeStorage(STORAGE_NEXT_KEY, '1');
    setLoaderMode('transition');
    setVisible(true);
  };

  const isInternalNav = (anchor) => {
    if (!(anchor instanceof HTMLAnchorElement)) return false;
    if (anchor.hasAttribute('download')) return false;
    if (anchor.target && anchor.target !== '_self') return false;
    if (anchor.getAttribute('role') === 'button') return false;

    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return false;
    }
    if (href.startsWith('#')) return false;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return false;
    }

    if (url.origin !== window.location.origin) return false;

    if (
      url.pathname === window.location.pathname &&
      url.search === window.location.search &&
      (!url.hash || url.hash === window.location.hash)
    ) {
      return false;
    }

    return true;
  };

  if (shouldStartVisible) {
    setLoaderMode(mode);
    setVisible(true);
  } else {
    setLoaderMode('');
    setVisible(false);
    removeStorage(STORAGE_NEXT_KEY);
  }

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!isInternalNav(anchor)) return;

      showForOutgoingNavigation();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.target && form.target !== '_self') return;
      if (form.getAttribute('data-mk-no-loader') != null) return;

      // These forms add items with fetch and intentionally prevent native navigation.
      // Showing the page overlay for them would leave the screen blocked while the
      // cart drawer waits for the Ajax response.
      const isAsyncCartForm =
        form.matches('[data-type="add-to-cart-form"], [data-mk-product-form], [data-mk-shipping-form], .mk-cta__buy-now-form') ||
        form.closest('product-form-component') !== null;
      if (isAsyncCartForm) return;

      showForOutgoingNavigation();
    },
    true
  );

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      pageLoaded = true;
      scheduleHide();
    },
    { once: true }
  );

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    pageLoaded = true;
    removeStorage(STORAGE_NEXT_KEY);
    hideNow();
  });

  if (pageLoaded) {
    scheduleHide();
  }
})();
